import {
  DialogButtons,
  DialogResults,
  getCustomTag,
  sendNotification,
  useApiClient,
  useTranslation,
} from '@msdk/common'
import { Entity, EntityMetadata, readBaseMetadataByName } from '@msdk/definitions'
import { ButtonPrimary, COLORS, styled } from '@msdk/ui'
import React from 'react'

import { useDialogModal } from '../../hooks'

const DeleteButtonStyled = styled(ButtonPrimary)`
  background-color: ${COLORS.error.main};
  color: white;

  &:hover {
    background-color: ${COLORS.error.dark};
  }

  margin-right: 5px;
`

interface Props {
  selectedRows: Entity[]
  reload: () => void
}

export function DeleteButton(props: Props) {
  const t = useTranslation()
  const api = useApiClient()
  const { createModal } = useDialogModal()

  const handleClick = async () => {
    const title = t('LBL_VIEW_TITLE_DELETE_ITEM', 'Are you sure you want to delete the selected item?')
    const message = t(
      'LBL_VIEW_MESSAGE_DELETE_ITEM',
      "This item will be permanently deleted. You can't undo this action",
    )
    const res = await createModal(title, message, DialogButtons.YesNo)
    if (res === DialogResults.Yes) {
      const promises: Promise<boolean>[] = []

      props.selectedRows.forEach((row) => {
        const baseMetadata = readBaseMetadataByName(row.entityName) as EntityMetadata
        const promise = (async () => {
          try {
            await api.entity.deleteEntity(baseMetadata.msProvider, row.entityName, row.id)
            return true
          } catch (error) {
            if (error instanceof Error) {
              const regex = /\{[^]*\}/
              const match = error.message.match(regex)

              if (match) {
                const jsonString = match[0]
                const errorObject = JSON.parse(jsonString)
                if (errorObject.status == '409') {
                  sendNotification({ message: errorObject.response, status: 'error' })
                  return false
                }
              }
            }

            const translated = t('CMPSR_ACTION_DELETE_ERROR', 'Delete failed')

            sendNotification({ message: translated, status: 'error' })
            return false
          }
        })()
        promises.push(promise)
      })

      await Promise.all(promises)
      props.reload()
    }
  }

  return (
    <DeleteButtonStyled
      onClick={handleClick}
      key="DELETE"
      disabled={props.selectedRows.length === 0}
      data-test-id={getCustomTag(['view', 'delete', 'forever', 'button'])}
    >
      {t('CMPSR_DELETE_FOREVER', 'Delete forever')}
    </DeleteButtonStyled>
  )
}
